// @ts-strict-ignore
import {Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from "@angular/core";
import {EventEmitterService} from "@event-emitter";
import {
	DeviceViewService,
	GamepadClient,
	LayoutVariants,
	SimBrowserCapabillitiesService,
	SimCameraService,
	SimConfigurationService,
	SimGamepadService,
	NotificationService,
	SimRecorderService,
	SimStreamObserverService,
	SimSystemExtendedService,
	SimUserExtendedService,
	SimUtilityService,
	SimWindowService,
	SystemHub,
} from "@services";
import {
	CameraLayoutMap,
	CameraParametersAxis,
	CameraLayoutType,
	CameraResolutionType,
	CameraViewMode,
	ConnectivityState,
	DeviceType,
	DisplayMode,
	LiveStreamCameraLayoutOptions,
	Permissions,
	PrivacyMode,
	UpdateErrorReason,
	VideoDestinationFormat,
	WebCameraView,
} from "@models";
import {TranslateService} from "@ngx-translate/core";
import Enumerable from "linq";
import {environment} from "../../../../environments/environment";
import {CdkDragDrop, CdkDragMove} from "@angular/cdk/drag-drop";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {Translations} from "@translations";
import {MatSliderDragEvent} from "@angular/material/slider";
//import {MenuAction} from "@common/components/action-menu/action-menu.component";

@UntilDestroy()
@Component({
	selector: "sim-recorder-camera-fin",
	templateUrl: "./recorder-camera.component.html",
	styleUrls: ["./recorder-camera.component.scss"],
	providers: [EventEmitterService],
	animations: [zoomInOnEnterAnimation({duration: 250}), zoomOutOnLeaveAnimation({duration: 250})],
	standalone: false,
})
export class RecorderCameraComponent implements OnInit, OnDestroy, OnChanges {
	T = Translations;
	DeviceType = DeviceType;
	Permissions = Permissions;
	VideoDestinationFormat = VideoDestinationFormat;
	CameraResolutionType = CameraResolutionType;
	SimUtilityService = SimUtilityService;
	ConnectivityState = ConnectivityState;
	CameraViewMode = CameraViewMode;
	settings = SimConfigurationService.settings;
	LayoutVariants = LayoutVariants;
	protected readonly CameraLayoutType = CameraLayoutType;
	protected readonly CameraLayoutMap = CameraLayoutMap;

	public containerId: string = null;
	public containerClasses: string = null;
	@ViewChild("recorderCameraBaseContainer") containerElement;

	public get isThisCinemaMode() {
		return this.liveStreamCameraLayoutOptions?.cameraViewMode === this.screenIndex + 1 && !this.isSingleView;
	}

	public get shouldGamepadBeActivated() {
		return this.liveStreamCameraLayoutOptions.enableControl && this.controlsVisible && this.screen?.deviceType === DeviceType.PTZCamera;
	}

	public isThisFullscreen = false;
	public isAnyFullscreen = false;

	public controlsVisible = false;

	@Input() screen: WebCameraView;
	@Input() liveStreamCameraLayoutOptions: LiveStreamCameraLayoutOptions;
	@Input() isSingleView = false;

	@Input() screens: WebCameraView[];
	@Input() isDragPreview = false;
	@Input() screenIndex: number;

	public defaultStyle: any;

	public onResizeBookmarkPane: Function;

	public currentNetworkSpeed: "slow" | "medium" | "fast" = "fast";
	public currentlyAbsoluteZooming = false;
	public currentlyRelativeZooming = false;

	private ptzChangeActivated = false;
	private cameraPosIsChanging = false;

	public zoomValueForeground: number;
	public zoomValueBackground: number;

	public availablePresetNumbers: number[] = [];

	public gamepadClient: GamepadClient = null;

	public currentScreenUrl: string;
	public currentScreenOutputFormat: VideoDestinationFormat = VideoDestinationFormat.Mjpeg;

	public currentScreenOptions: {
		cameraId: string;
		ipAddress: string;
		url: string;
		anonymousAccessAllowed: boolean;
		status: ConnectivityState;
	} = null;

	private OfflineVideoUrl: string;

	public basehref = environment.baseHref;
	public selectedStreamName = "0";
	public subStreamSelected: boolean;
	public subStreamViewMode: CameraViewMode = CameraViewMode.AllScreens;

	currentCameraParameters: CameraParametersAxis;
	activePTZPosition: number = -1;
	selectedPTZPosition: number = -1;

	ptzInEditMode: boolean = false;
	isTwoScreen: boolean;
	privacyModeEnabled: boolean;
	showCamInfo: boolean;

	public isRotating = false;

	public cameraPositionChecker: ReturnType<typeof setInterval> = null;

	public onDragMove(event: CdkDragMove<WebCameraView>): void {
		const nodeMovePreview = new ElementRef<HTMLElement>(document.getElementById("dragCameraContainerId"));
		const cameraComponent = document.getElementById(this.screen.id);

		if (nodeMovePreview?.nativeElement?.style.width === "") {
			if (nodeMovePreview?.nativeElement) {
				nodeMovePreview.nativeElement.style.width = cameraComponent.getBoundingClientRect().width + "px";
			}
		}

		if (!nodeMovePreview?.nativeElement?.getAttribute("offsetX")) {
			const xPos = event.pointerPosition.x - cameraComponent.getBoundingClientRect().x;
			const yPos = event.pointerPosition.y - cameraComponent.getBoundingClientRect().y;

			nodeMovePreview.nativeElement.setAttribute("offsetX", xPos + "");
			nodeMovePreview.nativeElement.setAttribute("offsetY", yPos + "");
		}

		if (nodeMovePreview?.nativeElement) {
			const xPos = event.pointerPosition.x - parseInt(nodeMovePreview.nativeElement.getAttribute("offsetX"), 10);
			const yPos = event.pointerPosition.y - parseInt(nodeMovePreview.nativeElement.getAttribute("offsetY"), 10);

			nodeMovePreview.nativeElement.style.transform = `translate3d(${xPos + "px"}, ${yPos + "px"}, 0)`;
		}
	}

	constructor(
		private _simBrowserCapabilityService: SimBrowserCapabillitiesService,
		private _simUtilityService: SimUtilityService,
		private _emitterService: EventEmitterService,
		private _simGamepadService: SimGamepadService,
		private _simCameraService: SimCameraService,
		private _simNotificationService: NotificationService,
		private _simStationRecorderProxyService: SimRecorderService,
		private _simUserService: SimUserExtendedService,
		private _simSystemService: SimSystemExtendedService,
		private _systemHub: SystemHub,
		public translate: TranslateService,
		public _simWindowService: SimWindowService,
		public _streamObserverService: SimStreamObserverService,
		public simDeviceViewService: DeviceViewService,
	) {
		this.containerId = SimUtilityService.createGuid();
		this.OfflineVideoUrl = SimConfigurationService.settings.offlineVideoUrl;
		this.privacyModeEnabled = SimConfigurationService.settings.privacyMode != PrivacyMode.Disabled;
		this._simWindowService.isTwoScreen.pipe(untilDestroyed(this)).subscribe((result: boolean) => {
			this.isTwoScreen = result;
		});
	}

	ngOnInit() {
		this.currentNetworkSpeed = this._simBrowserCapabilityService.getNetworkConnectionSpeed();

		this._emitterService.onNetworkChanged.subscribe((args) => {
			const speed = this._simBrowserCapabilityService.getNetworkConnectionSpeed();

			if (this.currentNetworkSpeed !== speed) {
				this.currentNetworkSpeed = speed;

				this.updateScreenUrl();
			}
		});

		// SIMSOFT-6774
		this._emitterService.onCameraStateChanged.subscribe((args) => {
			if (this.screen && this.screen.id === args.cameraState.id) {
				if (this.screen.status !== args.cameraState.status) {
					this.screen.status = args.cameraState.status;
					this.refreshCamera();
				}
				this.screen.deviceInformation = args.cameraState.deviceInformation;
			}
		});

		this._emitterService.onCameraSettingsChanged.subscribe((args) => {
			const cameraId = args.cameraId;

			if (cameraId && cameraId === this.screen.id) {
				// Reload camera view if something has changed, but wait for the change to take effect
				console.log("Reloading camera due to rotation");

				this.refreshCamera();
			}
		});

		this._emitterService.onSystemHubReconnected.subscribe(async (args) => {
			this.setStreamObserverState(true);
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["liveStreamCameraLayoutOptions"]) {
			// set default values
			if (
				this.liveStreamCameraLayoutOptions.enableControl === undefined ||
				this.liveStreamCameraLayoutOptions.enableControl === null
			) {
				this.liveStreamCameraLayoutOptions.enableControl = true;
			}

			if (this.liveStreamCameraLayoutOptions.displayMode === null || this.liveStreamCameraLayoutOptions.displayMode === undefined) {
				this.liveStreamCameraLayoutOptions.displayMode = DisplayMode.Default;
			}

			// SIMSOFT-4697 ASturl 15.01.2020 Added permission check to prevent control
			const hasPermissionForChangingCamera = this._simUserService.hasPermission(Permissions.EnableChangingCamPosition);
			if (!hasPermissionForChangingCamera) {
				this.liveStreamCameraLayoutOptions.enableControl = false;
			}

			if (
				this.liveStreamCameraLayoutOptions.hideIfOtherCameraIsInFullscreen === undefined ||
				this.liveStreamCameraLayoutOptions.hideIfOtherCameraIsInFullscreen === null
			) {
				this.liveStreamCameraLayoutOptions.hideIfOtherCameraIsInFullscreen = true;
			}

			this.updateScreenClasses();

			if (
				this.liveStreamCameraLayoutOptions.cameraViewMode !== CameraViewMode.AllScreens &&
				this.liveStreamCameraLayoutOptions.enableControl
			) {
				this.setControlsVisible(true);
			}

			$(document).unbind("mouseup." + this.containerId);
			if (!this.liveStreamCameraLayoutOptions.hideIfOtherCameraIsInFullscreen) {
				$(document).on("mouseup." + this.containerId, this.handleDocumentClick.bind(this));
			}
		}

		if (changes["screen"]) {
			this.updateScreenUrl();
			this.updateScreenClasses();
			this.initializeHomePositions();
			this.refreshCameraParameters(true);
			this.setStreamObserverState(true);
		}

		if (changes["displayMode"]) {
			this.updateScreenUrl();
			this.updateScreenClasses();
		}

		this.checkGamepadActivation();
	}

	ngOnDestroy(): void {
		$(document).unbind("mouseup." + this.containerId);

		this.disableGamepad();
		if (this.cameraPositionChecker) {
			clearInterval(this.cameraPositionChecker);
		}

		this.setStreamObserverState(false);
	}

	public setControlsVisible(value: boolean) {
		this.controlsVisible = value;

		this.checkGamepadActivation();
		this.updateCameraPositionChecker();
	}

	private checkGamepadActivation() {
		if (this.shouldGamepadBeActivated) {
			this.enableGamepad();
		} else {
			this.disableGamepad();
		}
	}

	private updateCameraPositionChecker() {
		if (this.controlsVisible) {
			this.cameraPositionChecker = setInterval(() => {
				this.refreshCameraParameters();
			}, 500);
		} else {
			if (this.cameraPositionChecker) {
				clearInterval(this.cameraPositionChecker);
			}
		}
	}

	private setStreamObserverState(isObserving: boolean) {
		if (this.screen?.id !== this.SimUtilityService.emptyGuid() && this.screen?.id !== undefined) {
			this._streamObserverService.updateStreamObserverState({
				cameraId: this.screen?.id,
				isObserving: isObserving,
				observerId: this.containerId,
				connectionId: this._systemHub.connectionId,
			});
		}
	}

	// See:
	// https://stackoverflow.com/questions/1403615/use-jquery-to-hide-a-div-when-the-user-clicks-outside-of-it
	public handleDocumentClick(e: JQueryMouseEventObject) {
		const container = $("#" + this.containerId);

		// if the target of the click isn't the container nor a descendant of the container
		if (container && container.length === 1 && !container.is(e.target) && container.has(e.target).length === 0) {
			this.isThisFullscreen = false;
		}
	}

	public updateScreenClasses() {
		this.containerClasses = "";

		if (this.isThisCinemaMode) {
			this.containerClasses += "col-xs-12 col-md-12 col-lg-12";
		} else {
			this.containerClasses += this.liveStreamCameraLayoutOptions.defaultClasses;
		}
	}

	public updateScreenUrl() {
		this.currentScreenOutputFormat = this.getCameraOutputFormat();
		this.currentScreenUrl = this.getScreenUrl();
		this.currentScreenOptions = this.buildScreenOptions();

		if (this.screen.streamNames?.length > 0 && !this.subStreamSelected) {
			this.selectedStreamName = this.screen.streamNames[0];
		}
	}

	public buildScreenOptions(): {
		cameraId: string;
		ipAddress: string;
		url: string;
		anonymousAccessAllowed: boolean;
		status: ConnectivityState;
	} {
		return this.screen
			? {
					cameraId: this.screen.id,
					ipAddress: this.screen.ipAddress,
					anonymousAccessAllowed: this.screen?.configuration?.anonymousAccessAllowed ?? true,
					url: this.currentScreenUrl,
					status: this.screen.status,
				}
			: null;
	}

	public getScreenUrl() {
		if (this.screen) {
			if (this.currentScreenOutputFormat === VideoDestinationFormat.Mjpeg) {
				if (this.currentNetworkSpeed === "fast") {
					// 2019-03-28, Alex
					// Cinema-Mode should also use the bigger resolution.
					if (this.isThisFullscreen || this.isThisCinemaMode) {
						if (
							this.liveStreamCameraLayoutOptions.preferredFullscreenStream &&
							this.screen[this.liveStreamCameraLayoutOptions.preferredFullscreenStream]
						) {
							return (
								this.screen[this.liveStreamCameraLayoutOptions.preferredFullscreenStream] ||
								SimConfigurationService.settings.offlineVideoUrl
							);
						} else {
							return this.screen.streamUrl_Default || SimConfigurationService.settings.offlineVideoUrl;
						}
					} else {
						if (
							this.liveStreamCameraLayoutOptions.preferredStream &&
							this.screen[this.liveStreamCameraLayoutOptions.preferredStream]
						) {
							return (
								this.screen[this.liveStreamCameraLayoutOptions.preferredStream] ||
								SimConfigurationService.settings.offlineVideoUrl
							);
						} else {
							return this.screen.streamUrl_Default || SimConfigurationService.settings.offlineVideoUrl;
						}
					}
				} else {
					return SimConfigurationService.settings.offlineVideoUrl;
				}
			} else if (this.currentScreenOutputFormat === VideoDestinationFormat.AxisMediaStreamLibrary) {
				return this.getAxisStreamUrl();
			} else if (this.currentScreenOutputFormat === VideoDestinationFormat.AxisMediaStreamLibraryMjpeg) {
				return this.screen.rtspStreamUrl_RecordingWindow;
			} else if (this.currentScreenOutputFormat === VideoDestinationFormat.RtspWebRtc) {
				return this.screen.rtspStreamUrl_Default;
			}
		} else {
			return SimConfigurationService.settings.offlineVideoUrl;
		}
	}

	private getAxisStreamUrl() {
		const fullScreenStreamKey = this.liveStreamCameraLayoutOptions.preferredFullscreenStream;
		const streamKey = this.liveStreamCameraLayoutOptions.preferredStream;
		let streamUrl = null;

		if (this.isThisFullscreen || this.isThisCinemaMode) {
			let key = "rtspStreamUrl_" + CameraResolutionType?.[fullScreenStreamKey];
			if (fullScreenStreamKey && this.screen[key]) {
				streamUrl = this.screen[key] ?? this.OfflineVideoUrl;
			} else {
				streamUrl = this.screen.rtspStreamUrl_Default ?? this.OfflineVideoUrl;
			}
		} else {
			let key = "rtspStreamUrl_" + CameraResolutionType?.[streamKey];
			if (streamKey && this.screen[key]) {
				streamUrl = this.screen[key] ?? this.OfflineVideoUrl;
			} else {
				streamUrl = this.screen.rtspStreamUrl_Default ?? this.OfflineVideoUrl;
			}
		}

		return streamUrl.replace("axrtsp", "rtsp");
	}

	public enableGamepad() {
		if (
			this.screen &&
			this.screen.id &&
			this.screen.id !== SimUtilityService.emptyGuid() &&
			this.screen.deviceType === DeviceType.PTZCamera
		) {
			if (!this.gamepadClient) {
				this.gamepadClient = this._simGamepadService.getGamepadClient();
				this.gamepadClient.listen(
					(moveEvent) => {
						if (moveEvent && this.screen && this.controlsVisible && !this.gamepadClient.currentlyRequestMove) {
							this.gamepadClient.currentlyRequestMove = true;

							const speedX = Math.round(moveEvent.x * 100);
							const speedY = Math.round(moveEvent.y * 100);

							return this._simCameraService
								.ptzContinuousPanTiltMove({
									cameraId: this.screen.id,
									xSpeed: speedX,
									ySpeed: speedY,
								})
								.then((ptzContinuousPanTiltMoveResult) => {
									if (ptzContinuousPanTiltMoveResult) {
										this.gamepadClient.currentlyRequestMove = false;

										return {
											success: true,
											direction: moveEvent.direction,
										};
									} else {
										return {
											success: false,
											direction: moveEvent.direction,
										};
									}
								});
						} else {
							return Promise.resolve({
								success: false,
								direction: "neutral",
							});
						}
					},
					(zoomEvent) => {
						if (zoomEvent && this.screen && this.controlsVisible && !this.gamepadClient.currentlyRequestMove) {
							this.gamepadClient.currentlyRequestMove = true;

							const zoomSpeed = Math.round(zoomEvent.zoomSpeed * 100);

							return this._simCameraService
								.ptzContinousZoom({
									cameraId: this.screen.id,
									speed: zoomSpeed,
								})
								.then((ptzContinousZoomResult) => {
									if (ptzContinousZoomResult) {
										this.gamepadClient.currentlyRequestMove = false;

										return {
											success: true,
											zoom: zoomEvent.zoom,
										};
									} else {
										return {
											success: false,
											zoom: zoomEvent.zoom,
										};
									}
								});
						} else {
							return Promise.resolve({
								success: false,
								zoom: "neutral",
							});
						}
					},
					(homePositionEvent) => {
						console.log("homePositionEvent");

						if (homePositionEvent && this.screen && this.controlsVisible && !this.gamepadClient.currentlyRequestMove) {
							this.gamepadClient.currentlyRequestMove = true;

							return this._simCameraService
								.ptzGoToHomePosition({
									cameraId: this.screen.id,
									homePositionProfil: homePositionEvent.homePosition,
								})
								.then(() => {
									this.gamepadClient.currentlyRequestMove = false;

									return {success: true};
								});
						} else {
							return Promise.resolve({success: false});
						}
					},
				);
			}
		}
	}

	public disableGamepad() {
		if (this.gamepadClient) {
			this.gamepadClient.destroy();
			this.gamepadClient = null;
		}
	}

	private refreshCamera() {
		this.updateScreenUrl();
		this.updateScreenClasses();
	}

	private getCameraOutputFormat() {
		let outputFormat: VideoDestinationFormat = VideoDestinationFormat.Mjpeg;

		if (this.liveStreamCameraLayoutOptions.displayMode === DisplayMode.Auto) {
			if (this.liveStreamCameraLayoutOptions.preferredStream) {
				let resolutionType: CameraResolutionType = null;

				if (this.isThisCinemaMode || this.isThisFullscreen) {
					resolutionType = this.liveStreamCameraLayoutOptions.preferredFullscreenStream;
				} else {
					resolutionType = this.liveStreamCameraLayoutOptions.preferredStream;
				}

				if (this.screen?.configuration?.resolutions) {
					const screenResolution: any = Enumerable.from(this.screen.configuration.resolutions).firstOrDefault((resolution) => {
						return resolution.type === resolutionType;
					});

					if (screenResolution) {
						if (screenResolution.outputFormat !== VideoDestinationFormat.Default) {
							outputFormat = screenResolution.outputFormat;
						}
					}
				}
			}
		} else {
			switch (this.liveStreamCameraLayoutOptions.displayMode) {
				case DisplayMode.Default:
					outputFormat = VideoDestinationFormat.Mjpeg;
					break;
				case DisplayMode.AxisMediaStreamLibrary:
					outputFormat = VideoDestinationFormat.AxisMediaStreamLibrary;
					break;
				case DisplayMode.AxisMediaStreamLibrary:
					outputFormat = VideoDestinationFormat.AxisMediaStreamLibraryMjpeg;
					break;
				case DisplayMode.RtspWebRtc:
					outputFormat = VideoDestinationFormat.RtspWebRtc;
					break;
				case DisplayMode.None:
					outputFormat = VideoDestinationFormat.Default;
					break;
				default:
					outputFormat = VideoDestinationFormat.Mjpeg;
					break;
			}
		}

		return outputFormat;
	}

	private initializeHomePositions() {
		const isPTZCamera = this.isVideoSourceValid() && this.screen.deviceType === DeviceType.PTZCamera;

		const isOnline = this.screen.status === ConnectivityState.Online;

		if (isPTZCamera && isOnline) {
			this._simCameraService.ptzGetHomePositions(this.screen.id).then((ptzGetHomePositionsResult) => {
				this.availablePresetNumbers = ptzGetHomePositionsResult;
				this.activePTZPosition = -1;
			});
		}
	}

	private refreshCameraParameters(hardRefresh = false) {
		const isControllableCamera =
			this.isVideoSourceValid() &&
			(this.screen.deviceType === DeviceType.ZoomCamera || this.screen.deviceType === DeviceType.PTZCamera);
		const isOnline = this.screen.status === ConnectivityState.Online;

		if (isControllableCamera && isOnline) {
			this._simCameraService.getCurrentCameraParameters(this.screen.id).then((response) => {
				let cameraParameters = response.cameraParameters;
				let adjustedNewZoomValue = cameraParameters.zoomRatio * 100;

				let isChanging = this.zoomValueBackground !== adjustedNewZoomValue;

				//Once a PTZ related change has been triggered, we check for the event that it is no longer changing
				//This is necessary as a workaround for adjusting the slider on PTZ cameras where we do not get the zooming status from the API
				if (this.ptzChangeActivated && this.cameraPosIsChanging && !isChanging) {
					this.ptzChangeActivated = false;
				}

				this.cameraPosIsChanging = isChanging;

				//Only ZoomCameras can currently update the zooming parameter
				//During relative zoom from mousewheel/buttons, we can update the slider
				if (
					hardRefresh ||
					(!this.currentlyAbsoluteZooming &&
						!cameraParameters.cameraPositionAxis?.isZooming &&
						this.screen.deviceType === DeviceType.ZoomCamera) ||
					(!this.currentlyAbsoluteZooming && !this.ptzChangeActivated && this.screen.deviceType === DeviceType.PTZCamera)
				) {
					//&& this.screen.deviceType === DeviceType.ZoomCamera
					this.zoomValueForeground = adjustedNewZoomValue;
				}

				this.zoomValueBackground = adjustedNewZoomValue;
				//Preset numbers start with 1
				this.activePTZPosition = Math.max(-1, cameraParameters.currentPresetIndex - 1);

				this.currentCameraParameters = cameraParameters;
			});
		}
	}

	public getScreenStyle() {
		const style = {};

		if (!this.isThisCinemaMode && this.defaultStyle) {
			for (const cssKey in this.defaultStyle) {
				style[cssKey] = this.defaultStyle[cssKey];
			}
		}

		if (this.isAnyFullscreen && !this.isThisCinemaMode && this.liveStreamCameraLayoutOptions.hideIfOtherCameraIsInFullscreen) {
			style["display"] = "none";
		} else {
			style["display"] = "block";
		}

		return style;
	}

	private canControlCameras() {
		return this.liveStreamCameraLayoutOptions.enableControl && (this.hasDirectControlPermission() || this.controlsVisible);
	}

	public async zoomMouseWheel(event: WheelEvent) {
		let speed = -100;

		if (event.deltaY < 0) {
			speed = speed * -1;
		}

		if (!this.currentlyRelativeZooming) {
			this.currentlyRelativeZooming = true;

			await this.startZoom(speed);
			await SimUtilityService.delay(100);
			await this.stopZoom();
			this.currentlyRelativeZooming = false;
		}
	}

	private isVideoSourceValid(): boolean {
		return this.screen && this.screen.id !== SimUtilityService.emptyGuid();
	}

	public async startZoom(speed: number, continuous: boolean = true) {
		if (this.canControlCameras()) {
			if (this.isVideoSourceValid()) {
				this.currentlyRelativeZooming = true;
				if (this.screen.deviceType == DeviceType.PTZCamera) {
					if (continuous) {
						await this.ptzContinuousZoom(speed);
					} else {
						this.ptzZoom(speed * 5);
					}
					if (this.activePTZPosition !== -1) {
						this.ptzInEditMode = true;
					}
				} else {
					if (speed > 0) {
						await this.zoomIn();
					} else if (speed < 0) {
						await this.zoomOut();
					} else {
						//stop requests can be ignored for non ptz cams
					}
				}
			}
		}
	}

	public async stopZoom() {
		if (this.canControlCameras()) {
			if (this.screen && this.screen.id !== SimUtilityService.emptyGuid()) {
				if (this.screen.deviceType == DeviceType.PTZCamera) {
					await this.ptzContinuousZoom(0);
					this.currentlyRelativeZooming = false;
				}
			}
		}
	}

	private async zoomIn() {
		await this._simCameraService.zoomInCamera({cameraId: this.screen.id}).then((zoomInCameraResult) => {
			if (!zoomInCameraResult) {
				this._simNotificationService.notifyInfo(this.translate.instant(this.T.CAMERA_COULD_NOT_ZOOM));
			}
		});
	}

	private async zoomOut() {
		await this._simCameraService.zoomOutCamera({cameraId: this.screen.id}).then((zoomInCameraResult) => {
			if (!zoomInCameraResult) {
				this._simNotificationService.notifyInfo(this.translate.instant(this.T.CAMERA_COULD_NOT_ZOOM));
			}
		});
	}

	private async ptzContinuousZoom(speed: number) {
		await this._simCameraService
			.ptzContinousZoom({
				cameraId: this.screen.id,
				speed,
			})
			.then((ptzContinuousZoomResult) => {
				if (!ptzContinuousZoomResult) {
					this._simNotificationService.notifyInfo(this.translate.instant(this.T.CAMERA_COULD_NOT_ZOOM));
				} else {
				}
			});
	}

	private ptzZoom(zoomValue) {
		if (this.canControlCameras()) {
			this._simCameraService
				.ptzRelativeZoom({
					cameraId: this.screen.id,
					relativeZoomValue: zoomValue,
				})
				.then((ptzRelativeZoomResult) => {
					if (!ptzRelativeZoomResult) {
						this._simNotificationService.notifyInfo(this.translate.instant(this.T.CAMERA_COULD_NOT_ZOOM));
					}
				});
		}
	}

	public ptzGoToHomePosition(homePositionProfil: number) {
		if (this.liveStreamCameraLayoutOptions.isStageDesigner && this.availablePresetNumbers.indexOf(homePositionProfil) < 0) {
			this.selectedPTZPosition = homePositionProfil - 1;
			this.availablePresetNumbers.push(homePositionProfil);
			this.saveCurrentPtzPos();
		} else {
			if (this.ptzInEditMode) {
				this.ptzInEditMode = false;
				this.activePTZPosition = -1;
			}
			if (this.screen && this.screen.id && this.screen.id !== SimUtilityService.emptyGuid()) {
				this._simCameraService
					.ptzGoToHomePosition({
						cameraId: this.screen.id,
						homePositionProfil,
					})
					.then((ptzContinousZoomResult) => {
						if (!ptzContinousZoomResult) {
							this._simNotificationService.notifyInfo(this.translate.instant(this.T.CAMERA_COULD_NOT_ZOOM));
						}
						this.activePTZPosition = homePositionProfil - 1;
						this.selectedPTZPosition = homePositionProfil - 1;
						this.ptzChangeActivated = true;
					});
			}
		}
	}

	public ptzCenter(x, y, imageWidth, imageHeight) {
		if (this.screen && this.screen.id && this.screen.id !== SimUtilityService.emptyGuid()) {
			x = Math.round(x);
			y = Math.round(y);
			imageWidth = Math.round(imageWidth);
			imageHeight = Math.round(imageHeight);

			let speed = this.screen?.configuration?.panTiltSpeed;
			//null, undefined or 0
			if (!speed) {
				speed = 100;
			}

			this._simCameraService
				.ptzCenter({
					cameraId: this.screen.id,
					x,
					y,
					imageWidth,
					imageHeight,
					speed,
				})
				.then((ptzCenterResult) => {
					if (!ptzCenterResult) {
						this._simNotificationService.notifyInfo(this.translate.instant(this.T.CAMERA_COULD_NOT_MOVE_CAMERA));
					}

					if (this.activePTZPosition !== -1) {
						this.ptzInEditMode = true;
					}
				});
		}
	}

	public focusCamera(cameraId: string) {
		if (cameraId && cameraId !== SimUtilityService.emptyGuid()) {
			return this._simCameraService.focusCamera({cameraId}).then((result) => {
				if (result) {
					if (!result.success) {
						this._simNotificationService.notifyError(result.message);
					}
				} else {
					this._simNotificationService.notifyError(this.translate.instant(this.T.STAGE_DESIGNER_RELAY_REQUEST_ERROR));
				}
			});
		} else {
			this._simNotificationService.notifyError(this.translate.instant(this.T.STAGE_DESIGNER_SELECT_FIRST_CAMERA_ERROR));
		}
	}

	public toggleFullscreen($event?: MouseEvent) {
		if ($event) {
			$event.stopPropagation();
			$event.preventDefault();
		}

		if (!this.isThisFullscreen) {
			this.containerElement.nativeElement.requestFullscreen().then(() => {
				this.enableFullscreen();
				this.updateScreenUrl();
			});
		} else {
			document.exitFullscreen().then(() => {
				this.disableFullscreen();
				this.updateScreenUrl();
			});
		}
	}

	public onFullscreenChange() {
		// case occurs if the user exits the fullscreen by pressing escape
		if (!document.fullscreenElement && this.isThisFullscreen) {
			this.disableFullscreen();
		}
	}

	private disableFullscreen() {
		this.isThisFullscreen = false;
		if (!this.shouldGamepadBeActivated) {
			this.disableGamepad();
		}
	}

	private enableFullscreen() {
		this.isThisFullscreen = true;
		if (this.liveStreamCameraLayoutOptions.enableControl) {
			this.enableGamepad();
		}
	}

	public async toggleCinemaMode($event?: MouseEvent) {
		if ($event) {
			$event.stopPropagation();
			$event.preventDefault();
		}

		if (this.liveStreamCameraLayoutOptions.isStageDesigner) {
			if (this.liveStreamCameraLayoutOptions.cameraViewMode === CameraViewMode.AllScreens) {
				this.liveStreamCameraLayoutOptions.cameraViewMode = this.screenIndex + 1;
			} else {
				this.liveStreamCameraLayoutOptions.cameraViewMode = CameraViewMode.AllScreens;
			}
		} else {
			await this._simStationRecorderProxyService.toggleCameraViewMode({
				cameraViewMode: <CameraViewMode>(this.screenIndex + 1),
			});
		}
	}

	@HostListener("document:keyup.escape")
	public async disableCinemaMode() {
		if (this.liveStreamCameraLayoutOptions.cameraViewMode !== CameraViewMode.AllScreens) {
			if (this.liveStreamCameraLayoutOptions.isStageDesigner) {
				this.liveStreamCameraLayoutOptions.cameraViewMode = CameraViewMode.AllScreens;
			} else {
				await this._simStationRecorderProxyService.toggleCameraViewMode({cameraViewMode: <CameraViewMode>(this.screenIndex + 1)});
			}
		}
	}

	public handleScreenClick($event: MouseEvent) {
		if (this.canControlCameras()) {
			// PTZ Control should be possible even if the control bar is not visible.
			// See: SIMSOFT-3055
			if (this.screen.deviceType === DeviceType.PTZCamera) {
				const x = $event.offsetX,
					y = $event.offsetY,
					imageWidth = Math.floor($($event.currentTarget).width()),
					imageHeight = Math.floor($($event.currentTarget).outerHeight());

				this.ptzCenter(x, y, imageWidth, imageHeight);
			}
		}

		// SIMSOFT-4891 ASturl 01.04.2020
		// We do not want the click to trigger the OSCE room click if we're currently changing the camera
		if (this.controlsVisible) {
			$event.stopPropagation();
		}
	}

	private hasDirectControlPermission(): boolean {
		return this._simUserService.hasPermission(Permissions.EnableChangingCamPositionDirectlyInSingleRecording);
	}

	onDropListDropped($event: CdkDragDrop<WebCameraView[], WebCameraView[], WebCameraView>) {
		if ($event.previousContainer != $event.container) {
			this._emitterService.onCameraScreenSwitched.next({
				camera: $event.item.data,
				position: this.screenIndex,
			});
		}
	}

	getConnectedDropListIds(): string[] {
		return Enumerable.from(this.screens)
			.select((x) => x.id ?? x["fakeId"])
			.concat(["stageDesignerVideoSources"])
			.toArray();
	}

	saveCurrentPtzPos() {
		if (this.screen && this.screen.id && this.screen.id !== SimUtilityService.emptyGuid()) {
			this._simCameraService
				.ptzSetHomePosition({
					cameraId: this.screen.id,
					homePositionProfil: this.selectedPTZPosition + 1,
				})
				.then((result) => {
					if (!result) {
						this._simNotificationService.notifyInfo(this.translate.instant(this.T.CAMERA_COULD_NOT_ZOOM));
					}
					this.ptzInEditMode = false;
					this._simNotificationService.notifySuccess("Successfully saved PTZ Profile");
				});
		}
	}

	discardCurrentPTZPos() {
		this.ptzGoToHomePosition(this.selectedPTZPosition + 1);
	}

	setCurrentPositionAsHomePreset() {
		this._simCameraService.setCurrentPositionAsHomePreset({cameraId: this.screen.id}).then((result) => {
			if (!result) {
				this._simNotificationService.notifyInfo(this.translate.instant("Could not set current position as home position"));
			}
			this._simNotificationService.notifySuccess("Successfully set current position as home position");
		});
	}

	homePositionButtonClick() {
		if (this.currentCameraParameters?.homePreset) {
			//Go to home preset
			//this._simCameraService.ptzGoToHomePosition()
		} else {
			//Save current pos as home preset
			this.setCurrentPositionAsHomePreset();
		}
	}

	privacyPresetButtonClick() {
		if (this.currentCameraParameters?.privacyPreset) {
			//Go to privacy preset
		} else {
			//Save current pos as privacy preset
			this.setCurrentPositionAsPrivacyPreset();
		}
	}

	setCurrentPositionAsPrivacyPreset() {
		this._simCameraService.setCurrentPositionAsPrivacyPreset({cameraId: this.screen.id}).then((result) => {
			if (!result) {
				this._simNotificationService.notifyInfo(this.translate.instant("Could not set current position as privacy position"));
			}
			this._simNotificationService.notifySuccess("Successfully set current position as privacy position");
		});
	}

	public clearCameraHomePosition() {
		this._simCameraService.ptzClearHomePosition({cameraId: this.screen.id}).then((ptzRemoveHomePositionResult) => {
			if (!ptzRemoveHomePositionResult) {
				this._simNotificationService.notifyError(this.translate.instant(this.T.STAGE_DESIGNER_SET_CAMERA_HOMEPOSITION_ERROR));
			} else {
				//Camera Setting Changed Event over SignalR
			}
		});
	}

	public selectStream($event: MouseEvent, selectedIndex: number) {
		//If a substream has been selected (maximized), any click on the stream selection grid should return to the overview
		if (this.subStreamSelected) {
			selectedIndex = 0;
		}

		let streamName = this.screen?.streamNames[selectedIndex];

		this.subStreamSelected = selectedIndex > 0;
		this.selectedStreamName = streamName;

		this.subStreamViewMode = selectedIndex;

		console.log("Selected: " + selectedIndex + ", Name: " + streamName);

		$event.stopPropagation();
	}

	public rotateCamera180() {
		this.isRotating = true;

		return this._simCameraService
			.rotateCamera180({cameraId: this.screen.id})
			.then((result) => {
				if (result?.success) {
					//Camera Setting Changed Event over SignalR
				} else {
					if (result.reason == UpdateErrorReason.CameraInUseOnRecording) {
						this._simNotificationService.notifyError(this.translate.instant(this.T.STAGE_DESIGNER_CAMERA_ROTATION_BLOCKED));
					} else {
						this._simNotificationService.notifyError(this.translate.instant(this.T.STAGE_DESIGNER_ROTATE_CAMERA_ERROR));
					}
				}
			})
			.finally(() => {
				setTimeout(() => (this.isRotating = false), 1000);
			});
	}

	public sliderStartDrag($event: MatSliderDragEvent) {
		this.currentlyAbsoluteZooming = true;
	}

	async sliderStopDrag($event: MatSliderDragEvent) {
		this.zoomValueForeground = $event.value;

		await this._simCameraService
			.setZoomRatio({cameraId: this.screen.id, absoluteZoomRatio: this.zoomValueForeground / 100.0})
			.then((result) => {
				if (result?.success) {
					this.ptzChangeActivated = true;
					this.currentlyAbsoluteZooming = false;
					if (this.activePTZPosition !== -1) {
						this.ptzInEditMode = true;
					}
					this.refreshCameraParameters();
				} else {
					this._simNotificationService.notifyError(this.translate.instant(this.T.CAMERA_COULD_NOT_ZOOM));
				}
			});
	}

	formatThumbLabel(value: number): string {
		return `${value}%`;
	}
}
